import { useTranslation } from 'react-i18next';
import { BEHAVIOR_LEVELS_DEFAULT } from '@/data/mockCatalog';
import { getFundamentalName } from '@/hooks/useFundamentals';
import type { FundamentalCatalogItem } from '@/api/types';
import { fmtActionRange } from '@/utils/formatters';

// ── Lang definitions ──────────────────────────────────────────────────────────
export const TRANS_LANGS = [
  { code: 'en', flag: '🇺🇸', label: 'EN' },
  { code: 'fr', flag: '🇫🇷', label: 'FR' },
  { code: 'pt', flag: '🇧🇷', label: 'PT' },
  { code: 'de', flag: '🇩🇪', label: 'DE' },
  { code: 'nl', flag: '🇳🇱', label: 'NL' },
  { code: 'ro', flag: '🇷🇴', label: 'RO' },
] as const;
export type LangCode = typeof TRANS_LANGS[number]['code'];
export const ALL_LANG_CODES = TRANS_LANGS.map(l => l.code) as LangCode[];

// ── Per-language text ─────────────────────────────────────────────────────────
export interface LangText { nextlevel: string; title: string; desc: string; }
export function emptyLang(): LangText { return { nextlevel: '', title: '', desc: '' }; }

// ── Level helpers ─────────────────────────────────────────────────────────────
export const LEVEL_LABELS = BEHAVIOR_LEVELS_DEFAULT.map(l => l.label);
export const LEVEL_COLORS = BEHAVIOR_LEVELS_DEFAULT.map(l => l.color);

// ── Form state ────────────────────────────────────────────────────────────────
export interface FormState {
  open: boolean;
  editId: string | null;
  fund: string;
  thrMin: string;
  thrMax: string;
  nextlevel: string;
  title: string;
  desc: string;
  active: boolean;
  addedLangs: LangCode[];
  previewLang: string;
  langTexts: Record<string, LangText>;
  errors: string[];
}

export interface DeleteTarget { id: string; name: string; }

// ── Props ─────────────────────────────────────────────────────────────────────
interface ActionFormModalProps {
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
  onClose: () => void;
  onSave: () => void;
  fundamentals: FundamentalCatalogItem[];
}

const PREVIEW_ACTIVE_SEGS = 3;
const BAR_SEGS = 4;

export function ActionFormModal({ form, setForm, onClose, onSave, fundamentals }: ActionFormModalProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.split('-')[0];

  // ── Language handlers ─────────────────────────────────────────────────────────
  function addLang(code: LangCode) {
    if (form.addedLangs.includes(code)) return;
    if (form.addedLangs.length === 0 && import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log('[Actions] request translations via Azure Translator API', {
        nextlevel: form.nextlevel, title: form.title, desc: form.desc,
      });
    }
    setForm(f => ({
      ...f,
      addedLangs: [...f.addedLangs, code],
      langTexts: { ...f.langTexts, [code]: f.langTexts[code] ?? emptyLang() },
    }));
  }

  function removeLang(code: LangCode) {
    setForm(f => ({
      ...f,
      addedLangs: f.addedLangs.filter(l => l !== code),
      previewLang: f.previewLang === code ? 'es' : f.previewLang,
    }));
  }

  function setLangField(code: LangCode, field: keyof LangText, value: string) {
    setForm(f => ({
      ...f,
      langTexts: {
        ...f.langTexts,
        [code]: { ...(f.langTexts[code] ?? emptyLang()), [field]: value },
      },
    }));
  }

  // ── Preview helpers ───────────────────────────────────────────────────────────
  const previewFund = fundamentals.find(f => f.id === form.fund);
  const fundColor   = previewFund?.color ?? '#342F82';
  const thrMin      = parseFloat(form.thrMin) || 1;
  const thrMax      = parseFloat(form.thrMax) || 4;
  const ruleLabel   = form.thrMin && form.thrMax ? `${fmtActionRange(thrMin, thrMax)}` : '';

  const prevNextlevel = form.previewLang === 'es'
    ? form.nextlevel
    : (form.langTexts[form.previewLang]?.nextlevel ?? '');
  const prevTitle = form.previewLang === 'es'
    ? form.title
    : (form.langTexts[form.previewLang]?.title ?? '');

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="tif-box acf-wide-box" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="tif-header">
          <h3>
            <i className="fas fa-rocket" />
            {form.editId ? t('admin.actionEditTitle') : t('admin.actionNewTitle')}
          </h3>
          <button className="tif-close" onClick={onClose}>
            <i className="fas fa-times" />
          </button>
        </div>

        {/* Split */}
        <div className="acf-split">
          {/* ── FORM COLUMN ──────────────────────────────────────────── */}
          <div className="tif-body acf-form-col">

            {/* Fundamental */}
            <div className="tif-field">
              <label className="tif-label">{t('admin.tipFormFundamental')}</label>
              <select
                className="form-input tif-select"
                value={form.fund}
                onChange={e => setForm(f => ({ ...f, fund: e.target.value }))}
              >
                <option value="">{t('admin.tipFormSelectFundamental')}</option>
                {fundamentals.map(f => (
                  <option key={f.id} value={f.id}>{getFundamentalName(f, lang)}</option>
                ))}
              </select>
            </div>

            {/* Activation rule */}
            <div className="tif-field">
              <label className="tif-label">
                {t('admin.actionActivationRule')}
                {' '}<span style={{ color: '#aaa', fontWeight: 400 }}>
                  {t('admin.actionActivationRuleHint')}
                </span>
              </label>
              <div className="tif-range-row">
                <div className="tif-range-field">
                  <span className="tif-range-label">{t('admin.actionRuleMin')}</span>
                  <input
                    type="number" className="form-input tif-num"
                    min={1} max={4} step={0.01}
                    value={form.thrMin}
                    onChange={e => setForm(f => ({ ...f, thrMin: e.target.value }))}
                  />
                </div>
                <span className="tif-range-dash">—</span>
                <div className="tif-range-field">
                  <span className="tif-range-label">{t('admin.actionRuleMax')}</span>
                  <input
                    type="number" className="form-input tif-num"
                    min={1} max={4} step={0.01}
                    value={form.thrMax}
                    onChange={e => setForm(f => ({ ...f, thrMax: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            {/* Siguiente Nivel */}
            <div className="tif-field">
              <label className="tif-label">{t('admin.actionNextLevelText')}</label>
              <textarea
                className="form-input tif-textarea"
                rows={3}
                placeholder={t('admin.actionNextLevelPlaceholder')}
                value={form.nextlevel}
                onChange={e => setForm(f => ({ ...f, nextlevel: e.target.value }))}
              />
              <span className="tif-hint">{t('admin.actionNextLevelHint')}</span>
            </div>

            {/* Título */}
            <div className="tif-field">
              <label className="tif-label">
                {t('admin.actionFormTitle')}
                {' '}<span style={{ color: '#aaa', fontWeight: 400 }}>(⚡ link)</span>
              </label>
              <input
                type="text" className="form-input"
                placeholder={t('admin.actionFormTitlePlaceholder')}
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              />
            </div>

            {/* Descripción */}
            <div className="tif-field">
              <label className="tif-label">{t('admin.actionFormDescription')}</label>
              <textarea
                className="form-input tif-textarea"
                rows={3}
                placeholder={t('admin.actionFormDescPlaceholder')}
                value={form.desc}
                onChange={e => setForm(f => ({ ...f, desc: e.target.value }))}
              />
              <span className="tif-hint">{t('admin.actionFormDescHint')}</span>
            </div>

            {/* Traducciones */}
            <div className="tif-lang-section">
              <div className="tif-lang-header">
                <label className="tif-label">
                  <i className="fas fa-language" /> {t('admin.actionTranslations')}
                </label>
              </div>
              <div className="tif-lang-add-row">
                <span className="tif-lang-add-label">{t('admin.tipAddLang')}</span>
                {TRANS_LANGS.map(l => (
                  <button
                    key={l.code}
                    type="button"
                    className={`tif-lang-quick-btn${form.addedLangs.includes(l.code) ? ' tif-lang-used' : ''}`}
                    onClick={() => addLang(l.code)}
                    disabled={form.addedLangs.includes(l.code)}
                  >
                    {l.flag} {l.label}
                  </button>
                ))}
              </div>
              <div className="tif-lang-list">
                {form.addedLangs.map(code => {
                  const langDef = TRANS_LANGS.find(l => l.code === code)!;
                  const lt = form.langTexts[code] ?? emptyLang();
                  return (
                    <div key={code} className="tif-lang-block">
                      <div className="tif-lang-block-head">
                        <span className="tif-lang-block-label">{langDef.flag} {langDef.label}</span>
                        <button
                          className="tif-lang-block-del"
                          onClick={() => removeLang(code)}
                          title={t('common.delete')}
                        >
                          <i className="fas fa-times" />
                        </button>
                      </div>
                      <div className="tif-lang-block-body">
                        {/* Siguiente Nivel */}
                        <div className="tif-field">
                          <label className="tif-label">
                            {t('admin.actionNextLevelText')}
                            {' '}<small style={{ fontWeight: 400, opacity: 0.6 }}>({langDef.label})</small>
                          </label>
                          <textarea
                            className="form-input tif-textarea"
                            rows={2}
                            placeholder="Translated next level text…"
                            value={lt.nextlevel}
                            onChange={e => setLangField(code, 'nextlevel', e.target.value)}
                          />
                        </div>
                        {/* Título */}
                        <div className="tif-field">
                          <label className="tif-label">
                            {t('admin.actionFormTitle')}
                            {' '}<small style={{ fontWeight: 400, opacity: 0.6 }}>({langDef.label})</small>
                          </label>
                          <input
                            type="text" className="form-input"
                            placeholder="Translated action title…"
                            value={lt.title}
                            onChange={e => setLangField(code, 'title', e.target.value)}
                            style={{ fontSize: '0.82rem' }}
                          />
                        </div>
                        {/* Descripción */}
                        <div className="tif-field">
                          <label className="tif-label">
                            {t('admin.actionFormDescription')}
                            {' '}<small style={{ fontWeight: 400, opacity: 0.6 }}>({langDef.label})</small>
                          </label>
                          <textarea
                            className="form-input tif-textarea"
                            rows={2}
                            placeholder="Translated description…"
                            value={lt.desc}
                            onChange={e => setLangField(code, 'desc', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Active */}
            <div className="tif-field tif-active-row">
              <label className="switch">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={e => setForm(f => ({ ...f, active: e.target.checked }))}
                />
                <span className="slider" />
              </label>
              <span className="tif-active-label">{t('admin.tipFormActive')}</span>
            </div>

          </div>{/* end acf-form-col */}

          {/* ── PREVIEW COLUMN ──────────────────────────────────────── */}
          <div className="acf-preview-col">
            <div className="acf-preview-label">
              <span><i className="fas fa-eye" /> {t('admin.actionPreview')}</span>
              {/* Preview lang switcher */}
              <div className="acf-lang-switcher">
                <button
                  type="button"
                  className={`acf-lang-btn${form.previewLang === 'es' ? ' active' : ''}`}
                  onClick={() => setForm(f => ({ ...f, previewLang: 'es' }))}
                >
                  🌎 ES
                </button>
                {form.addedLangs.map(code => {
                  const langDef = TRANS_LANGS.find(l => l.code === code)!;
                  return (
                    <button
                      key={code}
                      type="button"
                      className={`acf-lang-btn${form.previewLang === code ? ' active' : ''}`}
                      onClick={() => setForm(f => ({ ...f, previewLang: code }))}
                    >
                      {langDef.flag} {langDef.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="acf-preview-context">{t('admin.actionPreviewContext')}</div>

            <div className="acf-preview-card">
              {/* Rule chip */}
              {ruleLabel && (
                <div className="acf-prev-rule-chip">
                  <i className="fas fa-sliders" />
                  {ruleLabel}
                </div>
              )}

              {/* Top row */}
              <div className="acf-prev-toprow">
                <div
                  className="acf-prev-icon"
                  style={{ background: `${fundColor}22`, border: `2px solid ${fundColor}44` }}
                >
                  <i className="fas fa-circle-half-stroke" style={{ color: fundColor, fontSize: '1rem' }} />
                </div>
                <div className="acf-prev-fundinfo">
                  <div className="acf-prev-fundname">
                    {previewFund ? getFundamentalName(previewFund, lang) : 'Fundamental'}
                  </div>
                  <div className="acf-prev-status" style={{ color: fundColor }}>
                    {LEVEL_LABELS[2]}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div className="acf-prev-score" style={{ color: fundColor }}>
                    2.5<span className="acf-prev-denom">/4</span>
                  </div>
                </div>
              </div>

              {/* Bar: 3 colored + 1 gray */}
              <div className="acf-prev-bar">
                {Array.from({ length: BAR_SEGS }).map((_, i) => (
                  <div
                    key={i}
                    className="acf-prev-seg"
                    style={{ background: i < PREVIEW_ACTIVE_SEGS ? fundColor : '#e2e4e9' }}
                  />
                ))}
              </div>

              {/* Siguiente Nivel box */}
              {prevNextlevel
                ? (
                  <div className="acf-prev-next-box">
                    <div className="acf-prev-next-label">SIGUIENTE NIVEL</div>
                    <div className="acf-prev-next-text">{prevNextlevel}</div>
                  </div>
                )
                : (
                  <div className="acf-prev-placeholder">
                    ← {t('admin.actionNextLevelPlaceholder')}
                  </div>
                )
              }

              {/* Action row */}
              {prevTitle
                ? (
                  <div className="acf-prev-action-row">
                    <i className="fas fa-bolt" style={{ color: fundColor }} />
                    <span className="acf-prev-action-title" style={{ color: fundColor }}>
                      {prevTitle}
                    </span>
                  </div>
                )
                : (
                  <div className="acf-prev-placeholder">
                    ← {t('admin.actionFormTitlePlaceholder')}
                  </div>
                )
              }
            </div>
          </div>
        </div>{/* end acf-split */}

        {/* Footer */}
        <div className="tif-footer">
          {form.errors.length > 0 && (
            <div className="tif-save-errors">
              <strong>{t('admin.actionSaveError')}</strong>
              <ul>
                {form.errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </div>
          )}
          <div className="tif-footer-actions">
            <button className="btn btn--ghost" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button className="btn btn--primary" onClick={onSave}>
              <i className="fas fa-check" /> {t('admin.actionSave')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
